import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

interface State {
    input: string;
    separator: string;
    lowercase: boolean;
    transliterate: boolean;
    maxLength: number;
    copied: boolean;
}

const TRANSLIT: Record<string, string> = {
    à: 'a', á: 'a', â: 'a', ã: 'a', ä: 'a', å: 'a', æ: 'ae',
    ç: 'c', č: 'c',
    è: 'e', é: 'e', ê: 'e', ë: 'e',
    ì: 'i', í: 'i', î: 'i', ï: 'i',
    ð: 'd',
    ñ: 'n',
    ò: 'o', ó: 'o', ô: 'o', õ: 'o', ö: 'o', ø: 'o', œ: 'oe',
    ù: 'u', ú: 'u', û: 'u', ü: 'u',
    ý: 'y', ÿ: 'y',
    ß: 'ss',
    ž: 'z', š: 's',
};

export default class SlugGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = { input: 'Hello, world! This is my "blog post" — about café & co.', separator: '-', lowercase: true, transliterate: true, maxLength: 80, copied: false };

    private slugify(s: string): string {
        let out = s;
        if (this.state.transliterate) {
            out = out.split('').map(c => TRANSLIT[c.toLowerCase()] || c).join('');
            // Strip combining marks
            out = out.normalize('NFKD').replace(/[̀-ͯ]/g, '');
        }
        if (this.state.lowercase) out = out.toLowerCase();
        // Strip non-alphanumeric except whitespace
        out = out.replace(/[^a-zA-Z0-9\s_-]/g, '');
        // Collapse whitespace and underscores into the separator
        out = out.trim().replace(/[\s_-]+/g, this.state.separator);
        // Strip leading/trailing separator
        out = out.replace(new RegExp(`^${escapeRegex(this.state.separator)}+|${escapeRegex(this.state.separator)}+$`, 'g'), '');
        if (this.state.maxLength > 0 && out.length > this.state.maxLength) {
            out = out.substring(0, this.state.maxLength).replace(new RegExp(`${escapeRegex(this.state.separator)}+$`), '');
        }
        return out;
    }

    private get output(): string {
        return this.slugify(this.state.input);
    }

    copyOutput = () => {
        if (!this.output) return;
        navigator.clipboard.writeText(this.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { input, separator, lowercase, transliterate, maxLength, copied } = this.state;
        const out = this.output;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Input</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="slug-input">Text to slugify</label>
                        <textarea id="slug-input" rows={3} value={input}
                            onChange={e => this.setState({ input: e.target.value, copied: false })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="slug-sep">Separator</label>
                            <select id="slug-sep" value={separator}
                                onChange={e => this.setState({ separator: e.target.value, copied: false })}>
                                <option value="-">- (dash)</option>
                                <option value="_">_ (underscore)</option>
                                <option value=".">. (dot)</option>
                                <option value="">(none)</option>
                            </select>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="slug-len">Max length (0 = no limit)</label>
                            <input id="slug-len" type="number" inputMode="decimal" min={0} max={500} value={maxLength}
                                onChange={e => this.setState({ maxLength: Math.max(0, +e.target.value || 0), copied: false })} />
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={lowercase}
                            onChange={e => this.setState({ lowercase: e.target.checked, copied: false })} />
                        Lowercase
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={transliterate}
                            onChange={e => this.setState({ transliterate: e.target.checked, copied: false })} />
                        Transliterate accents (à → a, ñ → n)
                    </label>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Slug"
                previewMeta={out ? `${out.length} chars` : undefined}
                preview={<pre className={genStyles.previewArea} aria-label="Slug output">{out || '(empty input)'}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!out}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                }
            />
        );
    }
}

function escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
